import { FC, useEffect } from 'react';
import { create } from 'zustand';
import { useAnalyticsSync } from 'app/modules/analytics/hooks/useAnalyticsSync';
import { useAnalyticsAutoSyncOrchestration } from 'app/modules/analytics/hooks/useAnalyticsAutoSyncOrchestration';

export type AnalyticsSyncStatus = 'idle' | 'syncing' | 'success' | 'error';

export interface AnalyticsSyncPublicState {
  isSyncing: boolean;
  isShowingSuccess: boolean;
  status: AnalyticsSyncStatus;
  syncError: string | null;
  syncingPageIds: readonly number[];
  syncPageProgress: { completed: number; total: number } | null;
  syncTargetMatchesSelection: boolean;
  syncTargetLabel: string;
  syncTargetIsGroup: boolean;
  syncStartedAt: number | null;
  isSyncDriverMounted: boolean;
  requestSync: (options?: { userInitiated?: boolean }) => void;
  cancelSync: () => void;
  jumpToSyncTarget: () => void;
  dismissSyncError: () => void;
  _publish: (snapshot: Partial<AnalyticsSyncPublicState>) => void;
}

const noop = () => undefined;

/**
 * Public, consumer-facing sync state. Written to by the single
 * `AnalyticsSyncDriver`; read everywhere else via selectors — no Context.
 */
export const useAnalyticsSyncStore = create<AnalyticsSyncPublicState>(
  (set) => ({
    isSyncing: false,
    isShowingSuccess: false,
    status: 'idle',
    syncError: null,
    syncingPageIds: [],
    syncPageProgress: null,
    syncTargetMatchesSelection: true,
    syncTargetLabel: '',
    syncTargetIsGroup: false,
    syncStartedAt: null,
    isSyncDriverMounted: false,
    requestSync: noop,
    cancelSync: noop,
    jumpToSyncTarget: noop,
    dismissSyncError: noop,
    _publish: (snapshot) => set(snapshot),
  }),
);

/** Convenience for consumers that need several fields at once. */
export function useAnalyticsSyncState(): AnalyticsSyncPublicState {
  return useAnalyticsSyncStore();
}

/**
 * Headless, mounted once inside the analytics providers. Owns the single
 * instance of the sync state machine (mutation + polling + timers) and
 * publishes its state to `useAnalyticsSyncStore`.
 */
const AnalyticsSyncDriver: FC = () => {
  const sync = useAnalyticsSync();
  const publish = useAnalyticsSyncStore((state) => state._publish);

  useAnalyticsAutoSyncOrchestration({
    requestSync: sync.requestSync,
    isSyncing: sync.isSyncing,
    isShowingSuccess: sync.isShowingSuccess,
  });

  useEffect(() => {
    publish({ isSyncDriverMounted: true });
    return () => publish({ isSyncDriverMounted: false });
  }, [publish]);

  useEffect(() => {
    publish({
      isSyncing: sync.isSyncing,
      isShowingSuccess: sync.isShowingSuccess,
      status: sync.status,
      syncError: sync.syncError,
      syncingPageIds: sync.syncingPageIds,
      syncPageProgress: sync.syncPageProgress,
      syncTargetMatchesSelection: sync.syncTargetMatchesSelection,
      syncTargetLabel: sync.syncTargetLabel,
      syncTargetIsGroup: sync.syncTargetIsGroup,
      syncStartedAt: sync.syncStartedAt,
      requestSync: sync.requestSync,
      cancelSync: sync.cancelSync,
      jumpToSyncTarget: sync.jumpToSyncTarget,
      dismissSyncError: sync.dismissSyncError,
    });
  }, [
    publish,
    sync.isSyncing,
    sync.isShowingSuccess,
    sync.status,
    sync.syncError,
    sync.syncingPageIds,
    sync.syncPageProgress,
    sync.syncTargetMatchesSelection,
    sync.syncTargetLabel,
    sync.syncTargetIsGroup,
    sync.syncStartedAt,
    sync.requestSync,
    sync.cancelSync,
    sync.jumpToSyncTarget,
    sync.dismissSyncError,
  ]);

  return null;
};

export default AnalyticsSyncDriver;
